import { useState, useRef } from 'react'
import { router } from '@inertiajs/react'
import type { Topic } from '../../types'
import FloatingMenu from '../shared/FloatingMenu'

interface Props {
  topic: Topic
  teamId: number
  onEdit: () => void
  onResolve: () => void
  onConvert: () => void
  onPriority?: () => void
  onEdge?: (edge: 'top' | 'bottom') => void
  onDelete?: () => void
}

export default function TopicContextMenu({ topic, teamId, onEdit, onResolve, onConvert, onPriority, onEdge, onDelete }: Props) {
  const [isOpen, setIsOpen] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)

  const closeMenu = () => {
    setIsOpen(false)
    setShowDeleteConfirm(false)
  }

  const handleDelete = () => {
    router.delete(`/teams/${teamId}/topics/${topic.id}`, {
      preserveScroll: true
    })
    setIsOpen(false)
    setShowDeleteConfirm(false)
  }

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        aria-label={`Topic menu: ${topic.title}`}
        aria-expanded={isOpen}
        aria-haspopup="true"
        onClick={(e) => { e.stopPropagation(); setIsOpen(!isOpen) }}
        className="p-1.5 rounded hover:bg-[#351E1D]/5 transition-colors text-text-secondary hover:text-text-primary"
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <circle cx="12" cy="5" r="2" />
          <circle cx="12" cy="12" r="2" />
          <circle cx="12" cy="19" r="2" />
        </svg>
      </button>

      <FloatingMenu
        open={isOpen}
        onClose={closeMenu}
        anchorRef={triggerRef}
        width={224}
        onClick={e => e.stopPropagation()}
        className="bg-white border border-[#351E1D]/10 rounded-lg shadow-lg py-1 w-56"
      >
          {onPriority && <button className="block w-full px-4 py-2 text-left hover:bg-charcoal/5" onClick={() => { closeMenu(); onPriority() }}>{topic.priority ? 'Deprioritize' : 'Prioritize'}</button>}
          {onEdge && <>
            <button className="block w-full px-4 py-2 text-left hover:bg-charcoal/5" onClick={() => { closeMenu(); onEdge('top') }}>Move to Top</button>
            <button className="block w-full px-4 py-2 text-left hover:bg-charcoal/5" onClick={() => { closeMenu(); onEdge('bottom') }}>Move to Bottom</button>
          </>}
          <button
            onClick={() => { onEdit(); setIsOpen(false) }}
            className="flex items-center gap-2 w-full px-4 py-2 text-text-primary hover:bg-[#351E1D]/5 text-left"
            style={{ fontSize: '14px' }}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Edit
          </button>

          {topic.status !== 'resolved' && (
            <button
              onClick={() => { onResolve(); setIsOpen(false) }}
              className="flex items-center gap-2 w-full px-4 py-2 text-text-primary hover:bg-[#351E1D]/5 text-left"
              style={{ fontSize: '14px' }}
            >
              <svg className="w-4 h-4 text-[#15532E]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Resolve
            </button>
          )}

          {topic.status !== 'resolved' && (
            <button
              onClick={() => { onConvert(); setIsOpen(false) }}
              className="flex items-center gap-2 w-full px-4 py-2 text-text-primary hover:bg-[#351E1D]/5 text-left"
              style={{ fontSize: '14px' }}
            >
              <svg className="w-4 h-4 text-[#ED1C24]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              Convert to Action Item
            </button>
          )}

          <div className="border-t border-[#351E1D]/10 my-1" />

          {!showDeleteConfirm ? (
            <button
              onClick={() => { if (onDelete) { closeMenu(); onDelete() } else setShowDeleteConfirm(true) }}
              className="flex items-center gap-2 w-full px-4 py-2 text-[#ED1C24] hover:bg-[#ED1C24]/10 text-left"
              style={{ fontSize: '14px' }}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Delete
            </button>
          ) : (
            <div className="px-4 py-2 space-y-2">
              <p className="text-text-primary" style={{ fontSize: '14px' }}>Delete this topic?</p>
              <div className="flex gap-2">
                <button
                  onClick={handleDelete}
                  className="px-3 py-1 bg-[#ED1C24] text-white font-semibold rounded hover:bg-pinnacle-red-dark"
                  style={{ fontSize: '14px' }}
                >
                  Delete
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-3 py-1 text-text-secondary hover:text-text-primary"
                  style={{ fontSize: '14px' }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
      </FloatingMenu>
    </div>
  )
}
